import React, { useMemo, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, makeStyles } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CloseIcon from "@material-ui/icons/Close";
import ExploreOutlinedIcon from "@material-ui/icons/ExploreOutlined";
import LaunchIcon from "@material-ui/icons/Launch";

import useTranslation from "src/translations/useTranslation";
import { TravelAnalysisMessages } from "@shared/languages/interfaces";
import { DischargeSensorHealth, TravelCycleOutcomes } from "@shared/constants/travel-cycle-causes.enum";
import { TravelCycleTravelRow, TravelCycleTravelsResult, TravelCycleTravelsVehicle } from "@shared/interfaces/travel-cycle-analysis.interface";
import NewDataTable, { DataTableActions, DataTableColumns } from "@molecules/NewDataTable";
import ButtonTable from "@components/Button/ButtonTable";
import { sortByNatural, sortByText, sortByValue } from "@utils/data-table-sorting";
import utils from "@utils/useful-functions";

import { CYCLE_GROUP_OF, CYCLE_OUTCOMES, CYCLE_RATE_COLORS, CYCLE_STATE_LABELS, CycleFailureOutcome } from "../../constants";
import { formatCount, formatPercent, percentOf } from "../../cycleUtils";
import { CycleDrilldownView } from "../../hooks/useTravelCycleDrilldown";
import { CycleTable, DrilldownBody, DrilldownHeader } from "../../styles";
import TravelCoordinateViewModal from "./TravelCoordinateViewModal";

/** Height of the list inside the 90vh dialog, below its title, search and header row. */
const LIST_HEIGHT = "calc(90vh - 285px)";

/** Rows of a bar list in the side panel. */
const BAR_LIST_LIMIT = 12;

const useStyles = makeStyles(() => ({
  dialogPaper: { height: "90vh", maxHeight: "90vh" },
  dialogTitle: { padding: "16px 24px 12px" },
  dialogContent: { padding: "16px 24px", overflow: "hidden", display: "flex", flexDirection: "column" as const }
}));

const SENSOR_OUTCOMES: TravelCycleOutcomes[] = [
  TravelCycleOutcomes.SENSOR_STOPPED, TravelCycleOutcomes.SENSOR_INTERMITTENT, TravelCycleOutcomes.DISCHARGE_ONE_OFF
];

/** Color and label of the discharge sensor health. */
const HEALTH: Record<DischargeSensorHealth, { color: string; labelKey: TravelAnalysisMessages }> = {
  [DischargeSensorHealth.STOPPED]: { color: CYCLE_RATE_COLORS.critical, labelKey: TravelAnalysisMessages.sensorStopped },
  [DischargeSensorHealth.INTERMITTENT]: { color: CYCLE_RATE_COLORS.warning, labelKey: TravelAnalysisMessages.sensorIntermittent },
  [DischargeSensorHealth.OK]: { color: CYCLE_RATE_COLORS.ok, labelKey: TravelAnalysisMessages.sensorOk }
};

/** One truck of the outcome view. */
interface VehicleRow {
  vehicle: TravelCycleTravelsVehicle;
  count: number;
}

/** One line of a bar list. */
interface BarItem {
  name: string;
  count: number;
  color: string;
}

/** Props for the CycleDrilldownModal organism. */
interface CycleDrilldownModalProps {
  open: boolean;
  view: CycleDrilldownView | null;
  data: TravelCycleTravelsResult | null;
  loading: boolean;
  /** Travels of the whole filter, for the share of an outcome. */
  filterTotal: number;
  /** Period of the filter, as shown under the title. */
  periodLabel: string;
  onClose: () => void;
  onOpenOutcome: (outcome: TravelCycleOutcomes) => void;
  onOpenVehicle: (idVehicle: string, outcome?: TravelCycleOutcomes, fromOutcome?: boolean) => void;
  /** Open the standard travel detail modal. */
  onOpenTravel: (idTravel: string) => void;
}

/**
 * Drill-down of the production cycle, in two panels. For an outcome: what it is, what to do and where it
 * happens (by regional or plant) on the left; its trucks on the right. For a truck: its numbers, the
 * discharge sensor health and its failures by cause on the left; its failed travels, with the 5 states, the
 * travel detail and the coordinate view, on the right.
 */
const CycleDrilldownModal: React.FC<CycleDrilldownModalProps> = ({
  open, view, data, loading, filterTotal, periodLabel, onClose, onOpenOutcome, onOpenVehicle, onOpenTravel
}) => {

  const { t } = useTranslation();
  const classes = useStyles();
  const [coordinateTravel, setCoordinateTravel] = useState<TravelCycleTravelRow | null>(null);
  const [placeKind, setPlaceKind] = useState<"regional" | "plant">("regional");
  const entryOf = (outcome?: TravelCycleOutcomes | null) => CYCLE_OUTCOMES.find((e) => e.outcome === outcome);
  const outcomeName = (outcome?: TravelCycleOutcomes | null): string => {
    const entry = entryOf(outcome);

    return (entry ? t(entry.nameKey) : t(TravelAnalysisMessages.outcomeComplete)) ?? "";
  };
  const groupOf = (outcome: TravelCycleOutcomes) => CYCLE_GROUP_OF[outcome as CycleFailureOutcome];
  const isVehicleView = !!view?.idVehicle;
  const entry = entryOf(view?.outcome);
  const vehicle = data?.vehicles.find((v) => v.idVehicle === view?.idVehicle);
  const travels = data?.travels ?? [];
  const dateText = (date: Date | string | null) => (date ? String(utils.formatDateIfHave(date, "date")) : "—");

  /** Counts by a name, largest first, as bar list items. */
  const countBy = (names: (string | null)[], color: (name: string) => string): BarItem[] => {
    const counts = new Map<string, number>();

    names.forEach((name) => {
      const key = name ?? t(TravelAnalysisMessages.noRegional) ?? "";

      counts.set(key, (counts.get(key) || 0) + 1);
    });

    return Array.from(counts.entries()).map(([name, count]) => ({ name, count, color: color(name) })).sort((a, b) => b.count - a.count);
  };

  const placeBars = useMemo(() => countBy(
    travels.map((tr) => (placeKind === "regional" ? tr.regionalName : tr.plantName)),
    () => (entry ? groupOf(entry.outcome).color : "#597aa7")
  ), [travels, placeKind, entry]); // eslint-disable-line react-hooks/exhaustive-deps

  const causeBars = useMemo(() => {
    const counts = new Map<TravelCycleOutcomes, number>();

    travels.forEach((tr) => counts.set(tr.outcome, (counts.get(tr.outcome) || 0) + 1));

    return Array.from(counts.entries())
      .map(([outcome, count]) => ({ name: outcomeName(outcome), count, color: groupOf(outcome)?.color ?? "#597aa7" }))
      .sort((a, b) => b.count - a.count);
  }, [travels]); // eslint-disable-line react-hooks/exhaustive-deps

  const barList = (items: BarItem[]) => {
    const largest = Math.max(1, ...items.map((i) => i.count));

    return (
      <div className="bar-list">
        {items.slice(0, BAR_LIST_LIMIT).map((item) => (
          <div key={item.name} className="bar-row">
            <span>{item.name}</span>
            <b>{formatCount(item.count)}</b>
            <span className="bar-track"><i style={{ width: `${percentOf(item.count, largest)}%`, background: item.color }} /></span>
          </div>
        ))}
      </div>
    );
  };

  // ---------- Outcome view: trucks
  const vehicleRows: VehicleRow[] = isVehicleView || !data ? [] : data.vehicles
    .map((v) => ({ vehicle: v, count: travels.filter((tr) => tr.idVehicle === v.idVehicle).length }))
    .sort((a, b) => b.count - a.count);
  const vehicleColumns: DataTableColumns[] = [
    {
      title: t(TravelAnalysisMessages.columnVehicle),
      data: sortByNatural<VehicleRow>((row) => row.vehicle.code, (row) => ReactDOMServer.renderToString(
        <span><b>{row.vehicle.code}</b><span className="cell-caption">{row.vehicle.plate ?? ""}</span></span>
      )),
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnRegional),
      data: sortByText<VehicleRow>((row) => row.vehicle.mainRegionalName ?? "", (row) => row.vehicle.mainRegionalName ?? "—"),
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnMainPlant),
      data: sortByText<VehicleRow>((row) => row.vehicle.mainPlantName ?? "", (row) => row.vehicle.mainPlantName ?? "—"),
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnTravelsWithCause),
      data: sortByValue<VehicleRow>((row) => row.count, (row) => `<b>${formatCount(row.count)}</b>`),
      className: "dt-center",
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnShareOfVehicle),
      data: sortByValue<VehicleRow>((row) => percentOf(row.count, row.vehicle.travels), (row) => ReactDOMServer.renderToString(
        <span>
          {formatPercent(percentOf(row.count, row.vehicle.travels))}
          <span className="cell-caption">{`${t(TravelAnalysisMessages.wordOf)} ${formatCount(row.vehicle.travels)}`}</span>
        </span>
      )),
      className: "dt-center",
      filterable: false
    },
    ...(view?.outcome && SENSOR_OUTCOMES.includes(view.outcome) ? [{
      title: t(TravelAnalysisMessages.columnDischarges),
      data: sortByValue<VehicleRow>(
        (row) => percentOf(row.vehicle.dischargeDetected, row.vehicle.reachedJobsite),
        (row) => `${formatCount(row.vehicle.dischargeDetected)} ${t(TravelAnalysisMessages.wordOf)} ${formatCount(row.vehicle.reachedJobsite)} · `
          + formatPercent(percentOf(row.vehicle.dischargeDetected, row.vehicle.reachedJobsite), 0)
      ),
      filterable: false
    }] : []),
    {
      title: "",
      orderable: false,
      searchable: false,
      data: () => `<button type="button" class="details-button btn-vehicle-travels">${t(TravelAnalysisMessages.columnDetails)}</button>`,
      filterable: false
    }
  ];
  const vehicleActions: DataTableActions<VehicleRow>[] = [
    { ref: ".btn-vehicle-travels", callback: (row: VehicleRow) => onOpenVehicle(row.vehicle.idVehicle, view?.outcome, true) }
  ];

  // ---------- Truck view: failed travels
  const travelColumns: DataTableColumns[] = [
    {
      title: t(TravelAnalysisMessages.columnNumDoc),
      data: sortByNatural<TravelCycleTravelRow>((row) => row.numDoc ?? "", (row) => row.numDoc ?? "—"),
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnDate),
      data: sortByValue<TravelCycleTravelRow>(
        (row) => new Date(row.documentDate).getTime(),
        (row) => String(utils.formatDateIfHave(row.documentDate, "fullDateWithoutSeconds"))
      ),
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnCause),
      data: sortByText<TravelCycleTravelRow>((row) => outcomeName(row.outcome), (row) => ReactDOMServer.renderToString(
        <span className={`cause-pill${row.outcome === view?.outcome ? " highlighted" : ""}`}>
          <i style={{ background: groupOf(row.outcome)?.color }} />
          {outcomeName(row.outcome)}
        </span>
      )),
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnRegisteredStates),
      orderable: false,
      data: (row: TravelCycleTravelRow) => ReactDOMServer.renderToString(
        <span className="state-chips">
          {CYCLE_STATE_LABELS.map((label, i) => {
            const registered = row.registeredStates[i] === "1";

            return (
              <span
                key={label}
                className={registered ? "state-on" : "state-off"}
                title={`${t(label)}: ${t(registered ? TravelAnalysisMessages.stateRegistered : TravelAnalysisMessages.stateNotRegistered)}`}
              >
                {t(label)}
              </span>
            );
          })}
        </span>
      ),
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnActions),
      orderable: false,
      searchable: false,
      data: () => ReactDOMServer.renderToString(
        <span style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
          <ButtonTable className="btn-open-travel" aria-disabled><LaunchIcon fontSize="small" /></ButtonTable>
          <ButtonTable className="btn-coordinate-view" aria-disabled><ExploreOutlinedIcon fontSize="small" /></ButtonTable>
        </span>
      ),
      filterable: false
    }
  ];
  const travelActions: DataTableActions<TravelCycleTravelRow>[] = [
    { ref: ".btn-open-travel", callback: (row: TravelCycleTravelRow) => onOpenTravel(row.idTravel) },
    { ref: ".btn-coordinate-view", callback: (row: TravelCycleTravelRow) => setCoordinateTravel(row) }
  ];

  const health = vehicle ? HEALTH[vehicle.health] : HEALTH[DischargeSensorHealth.OK];
  const withThisCause = view?.outcome ? travels.filter((tr) => tr.outcome === view.outcome).length : 0;

  const outcomeSide = entry && data && (
    <div className="drill-side">
      <div className="tiles">
        <div className="tile"><span>{t(TravelAnalysisMessages.tileTravels)}</span><b>{formatCount(travels.length)}</b></div>
        <div className="tile">
          <span>{t(TravelAnalysisMessages.tileShareOfFilter)}</span>
          <b>{formatPercent(percentOf(travels.length, filterTotal))}</b>
        </div>
        <div className="tile"><span>{t(TravelAnalysisMessages.tileVehicles)}</span><b>{formatCount(data.vehicles.length)}</b></div>
      </div>
      <div>
        <div className="label">{t(TravelAnalysisMessages.tileGroup)}</div>
        <span className="group-pill" style={{ background: groupOf(entry.outcome).color }}>{t(groupOf(entry.outcome).nameKey)}</span>
      </div>
      <div className="text-block">
        <div className="label">{t(TravelAnalysisMessages.whatItIs)}</div>
        <p>{t(entry.descriptionKey)}</p>
      </div>
      <div className="text-block">
        <div className="label">{t(TravelAnalysisMessages.glossaryWhatToDo)}</div>
        <p>{t(entry.actionKey)}</p>
      </div>
      <div>
        <div className="list-title">
          <span>{placeKind === "regional" ? t(TravelAnalysisMessages.byRegional) : t(TravelAnalysisMessages.byPlant)}</span>
          <span className="mini-toggle" role="group">
            <button type="button" aria-pressed={placeKind === "regional"} onClick={() => setPlaceKind("regional")}>
              {t(TravelAnalysisMessages.toggleRegional)}
            </button>
            <button type="button" aria-pressed={placeKind === "plant"} onClick={() => setPlaceKind("plant")}>
              {t(TravelAnalysisMessages.togglePlant)}
            </button>
          </span>
        </div>
        {barList(placeBars)}
      </div>
    </div>
  );

  const vehicleSide = vehicle && (
    <div className="drill-side">
      <div className="tiles">
        <div className="tile"><span>{t(TravelAnalysisMessages.tileTravelsInPeriod)}</span><b>{formatCount(vehicle.travels)}</b></div>
        <div className="tile">
          <span>{t(TravelAnalysisMessages.tileWithFailure)}</span>
          <b>{formatCount(travels.length)} <small>{formatPercent(percentOf(travels.length, vehicle.travels))}</small></b>
        </div>
        {view?.outcome && <div className="tile"><span>{t(TravelAnalysisMessages.tileWithThisCause)}</span><b>{formatCount(withThisCause)}</b></div>}
        <div className="tile text"><span>{t(TravelAnalysisMessages.tilePlant)}</span><b>{vehicle.mainPlantName ?? "—"}</b></div>
        <div className="tile text wide">
          <span>{t(TravelAnalysisMessages.tileSensor)}</span>
          <b style={{ color: health.color }}>{t(health.labelKey)}</b>
          <small>
            {t(TravelAnalysisMessages.sensorDetail, {
              pct: formatPercent(percentOf(vehicle.dischargeDetected, vehicle.reachedJobsite), 0),
              reached: formatCount(vehicle.reachedJobsite),
              date: dateText(vehicle.lastUnloadDate)
            })}
          </small>
        </div>
      </div>
      <div>
        <div className="list-title"><span>{t(TravelAnalysisMessages.failuresByCause)}</span></div>
        {barList(causeBars)}
      </div>
    </div>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper" classes={{ paper: classes.dialogPaper }}>
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <DrilldownHeader>
            <div>
              {isVehicleView && view?.fromOutcome && view.outcome ? (
                <button type="button" className="back-button" onClick={() => onOpenOutcome(view.outcome as TravelCycleOutcomes)}>
                  <ArrowBackIcon fontSize="inherit" />
                  {t(TravelAnalysisMessages.backTo, { name: outcomeName(view.outcome) })}
                </button>
              ) : (
                <nav className="drilldown-crumb">
                  <span>{t(TravelAnalysisMessages.cycleDrilldownTitle)}</span>
                  {entry && !isVehicleView && (<><span>›</span><span>{t(groupOf(entry.outcome).nameKey)}</span></>)}
                </nav>
              )}
              <h2>{isVehicleView ? `${vehicle?.code ?? ""}${vehicle?.plate ? ` · ${vehicle.plate}` : ""}` : outcomeName(view?.outcome)}</h2>
              <p>{isVehicleView && vehicle?.mainRegionalName ? `${vehicle.mainRegionalName} · ${periodLabel}` : periodLabel}</p>
            </div>
            <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
          </DrilldownHeader>
        </DialogTitle>
        <DialogContent dividers className={classes.dialogContent}>
          {loading || !data ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><CircularProgress size={32} /></div>
          ) : (
            <DrilldownBody>
              {isVehicleView ? vehicleSide : outcomeSide}
              <div className="drill-main">
                <div className="list-title">
                  <span>
                    {isVehicleView
                      ? t(TravelAnalysisMessages.failedTravelsCount, { count: travels.length })
                      : t(TravelAnalysisMessages.vehiclesWithCause, { count: vehicleRows.length })}
                  </span>
                </div>
                <CycleTable className="fill">
                  {isVehicleView ? (
                    <NewDataTable
                      key={`${view?.idVehicle}-${view?.outcome}`}
                      data={travels}
                      columns={travelColumns}
                      actions={travelActions}
                      settings={{ order: [[1, "desc"]] }}
                      showPaging={false}
                      showInfo={false}
                      showSearching
                      scrollHeight={LIST_HEIGHT}
                    />
                  ) : (
                    <NewDataTable
                      key={view?.outcome}
                      data={vehicleRows}
                      columns={vehicleColumns}
                      actions={vehicleActions}
                      settings={{ order: [[3, "desc"]] }}
                      showPaging={false}
                      showInfo={false}
                      showSearching
                      scrollHeight={LIST_HEIGHT}
                    />
                  )}
                </CycleTable>
              </div>
            </DrilldownBody>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">{t(TravelAnalysisMessages.close)}</Button>
        </DialogActions>
      </Dialog>
      <TravelCoordinateViewModal
        open={!!coordinateTravel}
        onClose={() => setCoordinateTravel(null)}
        travelId={coordinateTravel?.idTravel || ""}
        vehicleDescription={vehicle ? `${vehicle.code}${vehicle.plate ? ` — ${vehicle.plate}` : ""}` : ""}
        numDoc={coordinateTravel?.numDoc}
      />
    </>
  );
};

export default CycleDrilldownModal;
